import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  BookOpen, 
  ExternalLink, 
  Loader2,
  Wifi,
  Shield,
  Building2,
  Home,
  Scale,
  Heart,
  GraduationCap,
  Truck,
  Car,
  Users,
  MessageCircle,
  Sparkles,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Star,
  Zap,
  Globe,
  Phone,
  CreditCard,
  FileText,
  MapPin
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import UpgradeModal from '../../components/UpgradeModal';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive resource data with affiliate tracking
const RESOURCE_SECTIONS = [
  {
    id: 'banking',
    title: 'Banking & Money Transfer',
    subtitle: 'Moving your money from US → Mexico seamlessly',
    icon: Building2,
    painPoint: "Can't open a bank without residency? Start with these digital solutions.",
    resources: [
      { name: 'Wise', desc: 'Best rates for US→MX transfers. Get your MXN account instantly.', url: 'https://wise.com/invite/', cta: 'Open Wise Account', highlight: true, tracking: 'wise' },
      { name: 'DolarApp', desc: 'Mexico-based, holds USD & MXN. Perfect for expats.', url: 'https://dfrfrg.com/dolarapp', cta: 'Get DolarApp', highlight: true, tracking: 'dolarapp' },
      { name: 'Ria Money Transfer', desc: 'Great for larger transfers. Lower fees than banks.', url: 'https://www.riamoneytransfer.com/', cta: 'Compare Rates', tracking: 'ria' },
      { name: 'Banorte', desc: 'Open with temporary residency. Best expat-friendly bank.', url: 'https://www.banorte.com/', cta: 'Bank Info', tracking: 'banorte' },
      { name: 'BBVA Mexico', desc: 'English app available. Good for payroll.', url: 'https://www.bbva.mx/', cta: 'Learn More', tracking: 'bbva' },
    ],
    guide: 'Start with Wise or DolarApp while waiting for residency, then transition to Banorte once approved.'
  },
  {
    id: 'immigration',
    title: 'Immigration & Residency',
    subtitle: 'Your path to legal residency in Mexico',
    icon: Scale,
    painPoint: "Visa uncertainty? Here's your step-by-step guide + direct links.",
    resources: [
      { name: 'INM Appointments', desc: 'Book your immigration appointment directly.', url: 'https://citas.inm.gob.mx/', cta: 'Book Appointment', highlight: true, tracking: 'inm' },
      { name: 'MexLaw', desc: 'Top immigration attorneys. English-speaking.', url: 'https://www.mexlaw.com/', cta: 'Consult Attorney', highlight: true, tracking: 'mexlaw' },
      { name: 'Easy Legal Mexico', desc: 'Affordable visa assistance for expats.', url: 'https://www.easylegalmx.com/', cta: 'Get Quote', tracking: 'easylegal' },
      { name: 'Anderson Immigration', desc: 'Complex cases & appeals.', url: 'https://www.immigrationlaw.com/', cta: 'Learn More', tracking: 'anderson' },
      { name: 'US Consulate Mexico', desc: 'Embassy services for US citizens.', url: 'https://mx.usembassy.gov/', cta: 'Embassy Info', tracking: 'usembassy' },
    ],
    guide: 'Apply for Temporary Residency at your nearest Mexican consulate, then complete the process at INM within 30 days of arrival.'
  },
  {
    id: 'car',
    title: 'Car Registration & Insurance',
    subtitle: 'Drive legally in Mexico without the headaches',
    icon: Car,
    painPoint: "Car paperwork nightmare? Full canje checklist + insurance quotes below.",
    resources: [
      { name: 'MexInsurance', desc: 'Top-rated Mexico car insurance. Instant quotes.', url: 'https://www.mexinsurance.com/', cta: 'Get Quote', highlight: true, tracking: 'mexinsurance' },
      { name: 'Baja Bound', desc: 'Best for border crossings & temporary imports.', url: 'https://www.bajabound.com/', cta: 'Compare Rates', highlight: true, tracking: 'bajabound' },
      { name: 'GNP Seguros', desc: 'Major Mexican insurer. Full coverage options.', url: 'https://www.gnp.com.mx/', cta: 'Get Quote', tracking: 'gnp' },
      { name: 'Qualitas', desc: 'Largest auto insurer in Mexico.', url: 'https://www.qualitas.com.mx/', cta: 'Learn More', tracking: 'qualitas' },
      { name: 'Temporary Import Guide', desc: 'Step-by-step for bringing your US car.', url: 'https://www.banjercito.com.mx/', cta: 'Import Guide', tracking: 'banjercito' },
    ],
    guide: 'Temporary import lasts 180 days. For permanent residency, do the "canje" (plate exchange) at your local traffic office.'
  },
  {
    id: 'moving',
    title: 'Moving & Customs',
    subtitle: 'Ship your belongings across the border',
    icon: Truck,
    painPoint: "Don't know what to ship? Here are trusted movers + customs forms.",
    resources: [
      { name: 'UPakWeShip', desc: 'Affordable container shipping. You pack, they ship.', url: 'https://www.upakweship.com/', cta: 'Get Quote', highlight: true, tracking: 'upakweship' },
      { name: 'Seven Seas Worldwide', desc: 'Door-to-door international moving.', url: 'https://www.sevenseasworldwide.com/', cta: 'Free Quote', highlight: true, tracking: 'sevenseas' },
      { name: 'MexMove', desc: 'US-Mexico specialists. Bilingual team.', url: 'https://mexmove.com/', cta: 'Contact', tracking: 'mexmove' },
      { name: 'Mexico Customs Guide', desc: 'What you can bring + duty-free limits.', url: 'https://www.sat.gob.mx/', cta: 'Customs Info', tracking: 'sat' },
    ],
    guide: 'Menaje de casa (household goods) can be imported duty-free within 6 months of getting residency. Keep receipts!'
  },
  {
    id: 'housing',
    title: 'Housing & Real Estate',
    subtitle: 'Find your perfect home in Mexico',
    icon: Home,
    painPoint: "Searching from abroad? Start with these trusted platforms.",
    resources: [
      { name: 'Inmuebles24', desc: 'Mexico\'s largest listings. Filter by furnished.', url: 'https://www.inmuebles24.com/', cta: 'Search Homes', highlight: true, tracking: 'inmuebles24' },
      { name: 'Airbnb Monthly', desc: 'Perfect for your first month while searching.', url: 'https://www.airbnb.com/', cta: 'Find Stays', tracking: 'airbnb' },
      { name: 'Vivanuncios', desc: 'Direct from owners. Great deals.', url: 'https://www.vivanuncios.com.mx/', cta: 'Browse', tracking: 'vivanuncios' },
      { name: 'Mundalo Real Estate', desc: 'Our team can help find your perfect property.', url: '/contact', cta: 'Contact Us', highlight: true, tracking: 'mundalo_realestate' },
    ],
    guide: 'Rent first for 3-6 months to learn neighborhoods before buying. Most leases require 1 month deposit + 1 month advance.'
  },
  {
    id: 'healthcare',
    title: 'Healthcare & Insurance',
    subtitle: 'Quality care at a fraction of US costs',
    icon: Heart,
    painPoint: "Need health coverage? Options for every budget.",
    resources: [
      { name: 'GNP Health Insurance', desc: 'Major Mexican insurer. English support.', url: 'https://www.gnp.com.mx/', cta: 'Get Quote', highlight: true, tracking: 'gnp_health' },
      { name: 'BUPA Mexico', desc: 'International coverage. Premium option.', url: 'https://www.bupasalud.com.mx/', cta: 'Compare Plans', tracking: 'bupa' },
      { name: 'SafetyWing', desc: 'Nomad insurance. No long-term commitment.', url: 'https://safetywing.com/nomad-insurance', cta: 'Monthly Plans', highlight: true, tracking: 'safetywing' },
      { name: 'Hospital Angeles', desc: 'Premium private hospitals across Mexico.', url: 'https://www.hospitalangeles.com/', cta: 'Locations', tracking: 'angeles' },
      { name: 'Doctoralia', desc: 'Find English-speaking doctors.', url: 'https://www.doctoralia.com.mx/', cta: 'Find Doctor', tracking: 'doctoralia' },
    ],
    guide: 'Most expats use private insurance + pay out-of-pocket for routine care (costs are 50-80% less than US).'
  },
  {
    id: 'schools',
    title: 'Schools & Family',
    subtitle: 'World-class education for your children',
    icon: GraduationCap,
    painPoint: "Relocating with kids? Find the right school.",
    resources: [
      { name: 'International Schools Database', desc: 'Filter by curriculum, location, language.', url: 'https://www.international-schools-database.com/', cta: 'Search Schools', highlight: true, tracking: 'intlschools' },
      { name: 'American School Foundation', desc: 'US curriculum in Mexico City.', url: 'https://www.asf.edu.mx/', cta: 'Learn More', tracking: 'asf' },
      { name: 'British School Mexico', desc: 'British curriculum with IB.', url: 'https://www.britishschool.edu.mx/', cta: 'Apply', tracking: 'british' },
    ],
    guide: 'Apply early! Top international schools have waitlists. Most require translated transcripts and entrance exams.'
  },
  {
    id: 'connectivity',
    title: 'Connectivity & VPN',
    subtitle: 'Stay connected to work and home',
    icon: Wifi,
    painPoint: "Need reliable internet + US streaming access?",
    resources: [
      { name: 'Telcel', desc: 'Largest carrier. Best coverage nationwide.', url: 'https://www.telcel.com/', cta: 'Plans', highlight: true, tracking: 'telcel' },
      { name: 'Starlink', desc: 'Satellite internet for remote areas.', url: 'https://www.starlink.com/', cta: 'Order', tracking: 'starlink' },
      { name: 'NordVPN', desc: 'Access US content. Protect your data.', url: 'https://nordvpn.com/risk-free-vpn/', cta: 'Get VPN', highlight: true, tracking: 'nordvpn' },
      { name: 'ExpressVPN', desc: 'Fast speeds. Great for streaming.', url: 'https://expressvpn.com/', cta: 'Try Free', tracking: 'expressvpn' },
      { name: 'Totalplay', desc: 'Fiber internet for urban areas.', url: 'https://www.totalplay.com.mx/', cta: 'Check Coverage', tracking: 'totalplay' },
    ],
    guide: 'Get a local SIM on day one (Telcel has best coverage). VPN is essential for US banking and streaming apps.'
  },
  {
    id: 'community',
    title: 'Expat Community & Language',
    subtitle: 'Build your new social network',
    icon: Users,
    painPoint: "Feeling isolated? Connect with fellow expats.",
    resources: [
      { name: 'InterNations', desc: 'Global expat network. Events & groups.', url: 'https://www.internations.org/mexico-city-expats', cta: 'Join Free', highlight: true, tracking: 'internations' },
      { name: 'Babbel Spanish', desc: 'Practical conversational Spanish.', url: 'https://www.babbel.com/spanish', cta: 'Start Learning', highlight: true, tracking: 'babbel' },
      { name: 'Meetup Mexico', desc: 'Find local interest groups.', url: 'https://www.meetup.com/', cta: 'Find Groups', tracking: 'meetup' },
      { name: 'Facebook Expat Groups', desc: 'City-specific expat communities.', url: 'https://www.facebook.com/groups/', cta: 'Search Groups', tracking: 'fb_expats' },
    ],
    guide: 'Learning basic Spanish opens doors. Even 10 minutes daily makes a huge difference in your quality of life.'
  },
  {
    id: 'utilities',
    title: 'Utilities & Local Setup',
    subtitle: 'Get everything set up in your new home',
    icon: Zap,
    painPoint: "Setting up utilities as a foreigner? Here's how.",
    resources: [
      { name: 'CFE (Electricity)', desc: 'National power company. Online billing.', url: 'https://www.cfe.mx/', cta: 'Setup Guide', highlight: true, tracking: 'cfe' },
      { name: 'SAT (Tax Authority)', desc: 'Get your RFC for invoices.', url: 'https://www.sat.gob.mx/', cta: 'RFC Info', tracking: 'sat_rfc' },
      { name: 'CURP Registration', desc: 'Mexican ID number for residents.', url: 'https://www.gob.mx/curp/', cta: 'Apply', tracking: 'curp' },
    ],
    guide: 'Most utilities can be transferred into your name with your lease and passport. RFC is needed for formal invoices.'
  }
];

const PortalResources = () => {
  const { user, loading } = useAuth();
  const [entitlements, setEntitlements] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('banking');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entRes, bpRes] = await Promise.all([
        fetch(`${API_URL}/api/portal/entitlements`, { credentials: 'include' }),
        fetch(`${API_URL}/api/portal/blueprint`, { credentials: 'include' })
      ]);
      
      if (entRes.ok) {
        const entData = await entRes.json();
        setEntitlements(entData);
      }
      
      if (bpRes.ok) {
        const bpData = await bpRes.json();
        setBlueprint(bpData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setDataLoading(false);
    }
  };

  // Track affiliate clicks
  const trackClick = async (resourceName, tracking) => {
    try {
      await fetch(`${API_URL}/api/analytics/resource-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          resource: resourceName, 
          tracking_id: tracking,
          timestamp: new Date().toISOString()
        })
      });
    } catch (err) {
      // Silent fail for analytics
    }
  };

  if (loading || dataLoading) {
    return (
      <PortalLayout activeTab="resources">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" />
        </div>
      </PortalLayout>
    );
  }

  if (!user) {
    return <Navigate to="/portal" />;
  }

  const firstName = user?.name?.split(' ')[0] || user?.first_name || 'Friend';
  const destination = blueprint?.destination || entitlements?.destination || 'Mexico';

  return (
    <PortalLayout activeTab="resources">
      {/* Personalized Header */}
      <div className="mb-8" data-testid="resource-hub-header">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-6 h-6 text-[#2CA8A0]" />
          <h1 className="text-2xl sm:text-3xl font-light text-white">
            Hi {firstName}, here's your personal guide
          </h1>
        </div>
        <p className="text-[#888] text-sm sm:text-base max-w-2xl">
          We're here with you every step of your move to <span className="text-[#2CA8A0]">{destination}</span>. 
          Click any link below and we'll walk you through it like old friends.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <div className="bg-[#111] border border-[#FFFFFF] p-4 text-center">
          <p className="text-2xl font-light text-[#2CA8A0]">40+</p>
          <p className="text-xs text-[#666]">Vetted Resources</p>
        </div>
        <div className="bg-[#111] border border-[#FFFFFF] p-4 text-center">
          <p className="text-2xl font-light text-[#2CA8A0]">10</p>
          <p className="text-xs text-[#666]">Categories</p>
        </div>
        <div className="bg-[#111] border border-[#FFFFFF] p-4 text-center">
          <p className="text-2xl font-light text-[#2CA8A0]">24/7</p>
          <p className="text-xs text-[#666]">Assistant</p>
        </div>
        <div className="bg-[#111] border border-[#FFFFFF] p-4 text-center">
          <p className="text-2xl font-light text-[#2CA8A0]">{destination}</p>
          <p className="text-xs text-[#666]">Your Destination</p>
        </div>
      </div>

      {/* Resource Sections */}
      <div className="space-y-4">
        {RESOURCE_SECTIONS.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;
          
          return (
            <div 
              key={section.id} 
              className="bg-[#111] border border-[#FFFFFF] overflow-hidden"
              data-testid={`resource-section-${section.id}`}
            >
              {/* Section Header */}
              <button
                onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-[#FFFFFF]/50 transition"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#2CA8A0]/10 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#2CA8A0]" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-medium text-sm sm:text-base">{section.title}</h3>
                    <p className="text-[#666] text-xs sm:text-sm">{section.subtitle}</p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-[#666]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#666]" />
                )}
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 sm:px-6 pb-6 border-t border-[#FFFFFF]">
                  {/* Pain Point Callout */}
                  <div className="bg-[#2CA8A0]/5 border border-[#2CA8A0]/20 p-4 mt-4 mb-6">
                    <p className="text-[#2CA8A0] text-sm font-medium">{section.painPoint}</p>
                  </div>

                  {/* Resources Grid */}
                  <div className="grid gap-3 mb-6">
                    {section.resources.map((resource, idx) => (
                      <a
                        key={idx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick(resource.name, resource.tracking)}
                        className={`flex items-center justify-between p-4 border transition hover:border-[#2CA8A0]/50 ${
                          resource.highlight 
                            ? 'bg-[#2CA8A0]/5 border-[#2CA8A0]/30' 
                            : 'bg-[#F9F6EF] border-[#FFFFFF]'
                        }`}
                        data-testid={`resource-link-${resource.tracking}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-sm font-medium">{resource.name}</span>
                            {resource.highlight && (
                              <Star className="w-3 h-3 text-[#2CA8A0] fill-[#2CA8A0]" />
                            )}
                          </div>
                          <p className="text-[#888] text-xs truncate">{resource.desc}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4 shrink-0">
                          <span className="text-[#2CA8A0] text-xs font-medium hidden sm:block">{resource.cta}</span>
                          <ExternalLink className="w-4 h-4 text-[#2CA8A0]" />
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* Guide Note */}
                  <div className="bg-[#FFFFFF] p-4 border-l-2 border-[#2CA8A0]">
                    <p className="text-[#aaa] text-sm leading-relaxed">
                      <span className="text-[#2CA8A0] font-medium">Pro tip:</span> {section.guide}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help CTA */}
      <div className="mt-8 bg-gradient-to-r from-[#2CA8A0]/10 to-transparent border border-[#2CA8A0]/30 p-6 sm:p-8" data-testid="help-cta">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-white font-medium mb-1">Need help right now?</h3>
            <p className="text-[#888] text-sm">Use the AI chat or upgrade for personal sessions with your Mundalo advisor.</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="flex items-center gap-2 bg-[#FFFFFF] border border-[#333] text-white px-4 py-2 text-sm hover:bg-[#222] transition"
              onClick={() => {/* Open AI chat */}}
            >
              <MessageCircle className="w-4 h-4" />
              AI Chat
            </button>
            <button 
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-2 bg-[#2CA8A0] text-black px-4 py-2 text-sm font-medium hover:bg-[#2CA8A0] transition"
              data-testid="resource-hub-upgrade-btn"
            >
              Upgrade
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <p className="text-[#555] text-xs text-center mt-8">
        All resources are vetted by our team. Some links may be affiliate partners - this helps keep Blueprint affordable.
      </p>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        userTier={entitlements?.tier || 'self_serve'}
        userEmail={user?.email}
        userName={user?.name}
      />
    </PortalLayout>
  );
};

export default PortalResources;
